/**
 * /admin/communities — super_admin panel (inherits the /admin gate).
 *
 * List of CUSTOM communities (migrations 378-383): standalone peers of the
 * tier communities with their own feed channels, chat, and membership. Each
 * row links to /admin/communities/$communityId — the full settings page for
 * profile, tier access, people, channels, and archive. Creation stays a
 * dialog; communities are never deleted, only archived.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArchiveRestore, ChevronRight, Globe2, Loader2, Plus } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  useCustomCommunities,
  useCustomCommunitiesForHub,
  useUnarchiveCustomCommunity,
} from '@/api/community-hooks';
import type { CustomCommunityRow } from '@/api/community';
import { getCommunityVisual } from '@/lib/hub-visual';

export const Route = createFileRoute('/admin/communities')({
  component: AdminCommunitiesPage,
});

function AdminCommunitiesPage() {
  const [tab, setTab] = useState<'active' | 'archived'>('active');

  const { data: active = [], isLoading: activeLoading } = useCustomCommunities();
  const { data: archived = [], isLoading: archivedLoading } = useCustomCommunities({ archived: true });
  const { data: hubCards = [] } = useCustomCommunitiesForHub();
  const countsById = useMemo(() => {
    const map = new Map<string, { members: number; posts: number }>();
    for (const card of hubCards) {
      map.set(card.id, { members: card.member_count, posts: card.post_count });
    }
    return map;
  }, [hubCards]);

  const unarchiveMutation = useUnarchiveCustomCommunity();

  const unarchive = (community: CustomCommunityRow) => {
    unarchiveMutation.mutate(
      { communityId: community.id },
      {
        onSuccess: () => toast.success(`"${community.name}" restored`),
        onError: (err) => toast.error(err?.message ?? 'Failed to unarchive community'),
      },
    );
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 sm:p-6 max-w-3xl space-y-4">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-primary" aria-hidden />
            Communities
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Standalone communities with their own channels, chat, and members — no
            tier required. Open a community to manage its profile, access, and
            channels.
          </p>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'active' | 'archived')}>
          <div className="flex items-center justify-between gap-2">
            <TabsList>
              <TabsTrigger value="active">
                Active{active.length > 0 ? ` (${active.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="archived">
                Archived{archived.length > 0 ? ` (${archived.length})` : ''}
              </TabsTrigger>
            </TabsList>
            <Button size="sm" asChild>
              <Link to="/admin/communities/new">
                <Plus className="w-4 h-4" aria-hidden />
                Create community
              </Link>
            </Button>
          </div>

          <TabsContent value="active" className="mt-4">
            {activeLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-hidden />
              </div>
            ) : active.length === 0 ? (
              <div className="py-10 text-center space-y-1.5 border border-border rounded-xl">
                <Globe2 className="w-5 h-5 mx-auto text-muted-foreground/50" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  No custom communities yet. Create one to get started.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden">
                {active.map((community) => (
                  <CommunityRow
                    key={community.id}
                    community={community}
                    counts={countsById.get(community.id)}
                  />
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="archived" className="mt-4">
            {archivedLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-hidden />
              </div>
            ) : archived.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground border border-border rounded-xl">
                No archived communities.
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden">
                {archived.map((community) => {
                  const visual = getCommunityVisual(community);
                  return (
                    <li key={community.id} className="flex items-center gap-3 p-3">
                      <Link
                        to="/admin/communities/$communityId"
                        params={{ communityId: community.id }}
                        className="flex items-center gap-3 min-w-0 flex-1 group"
                      >
                        <span
                          className="flex w-9 h-9 items-center justify-center rounded-lg text-white flex-shrink-0 opacity-60"
                          style={visual.bgStyle}
                        >
                          {visual.renderIcon('w-4 h-4')}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                            {community.name}
                          </p>
                          {community.description ? (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {community.description}
                            </p>
                          ) : null}
                        </div>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={() => unarchive(community)}
                        disabled={unarchiveMutation.isPending}
                      >
                        {unarchiveMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        ) : (
                          <ArchiveRestore className="w-4 h-4" aria-hidden />
                        )}
                        Unarchive
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Row — the whole row links to the community's settings page
// ─────────────────────────────────────────────────────────────────────────────

function CommunityRow({
  community,
  counts,
}: {
  community: CustomCommunityRow;
  counts?: { members: number; posts: number };
}) {
  const visual = getCommunityVisual(community);

  return (
    <li>
      <Link
        to="/admin/communities/$communityId"
        params={{ communityId: community.id }}
        className="flex items-center gap-3 p-3 group hover:bg-muted/40 transition-colors"
      >
        <span
          className="flex w-9 h-9 items-center justify-center rounded-lg text-white flex-shrink-0"
          style={visual.bgStyle}
        >
          {visual.renderIcon('w-4 h-4')}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
            {community.name}
          </p>
          <p className="text-xs text-muted-foreground line-clamp-1">
            {counts
              ? `${counts.members} member${counts.members === 1 ? '' : 's'} · ${counts.posts} post${counts.posts === 1 ? '' : 's'}`
              : community.description || '—'}
          </p>
        </div>
        <ChevronRight
          className="w-4 h-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0 transition-colors"
          aria-hidden
        />
      </Link>
    </li>
  );
}
